import { Link } from 'react-router-dom';
import { PortfolioThemeControl } from '../components/PortfolioThemeControl';
import { useEffect, useState } from 'react';
import {
  parseDeletionReceipt,
  summarizeDeletionReceipt,
  type DeleteMode,
  type DeletionReceipt,
  type ReceiptSection,
} from './settings-receipt';
import { PRIVATE_MEDIA_DB_NAME } from '../lib/private-visit-photo';
import { isAppsInTossChannel } from '../lib/channel';
import { LocalBackupPanel } from '../state/LocalBackupPanel';
import { useLocalState } from '../state/LocalStateProvider';

const LOCAL_DATABASES = ['anjabom_outbox', PRIVATE_MEDIA_DB_NAME] as const;

function deleteLocalDatabase(name: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(name);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
    request.onblocked = () => reject(new Error('database_blocked'));
  });
}

export function SettingsPage() {
  const { resetAfterDeletion } = useLocalState();
  const appsInToss = isAppsInTossChannel();
  const [cleared, setCleared] = useState(false);
  const [session, setSession] = useState(false);
  const [mode, setMode] = useState<DeleteMode | null>(null);
  const [receipt, setReceipt] = useState<DeletionReceipt | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    if (appsInToss) return;
    void fetch('/api/auth/session')
      .then((response) => response.json())
      .then((value) => setSession(Boolean((value as { user: unknown }).user)));
  }, [appsInToss]);

  const clear = async () => {
    if (!window.confirm('공개한 글은 내가 공개한 글에서 먼저 지워 주세요. 이 기기의 후보·기억·사진·공개 글 열쇠를 모두 지울까요? 서버의 공개 글은 지워지지 않고 되돌릴 수 없어요.')) return;
    setCleared(false); setError('');
    let clearedText = false;
    try {
    for (const key of Object.keys(localStorage)) {
      if (key.startsWith('anjabom.')) localStorage.removeItem(key);
    }
    if (Object.keys(localStorage).some((key) => key.startsWith('anjabom.')) || !resetAfterDeletion()) throw new Error('local_delete_unconfirmed');
    clearedText = true;
    try {
      for (const key of Object.keys(sessionStorage)) {
        if (key.startsWith('anjabom.')) sessionStorage.removeItem(key);
      }
      if (Object.keys(sessionStorage).some((key) => key.startsWith('anjabom.'))) throw new Error('draft_delete_unconfirmed');
    } catch { setError('이 탭의 작성 중인 초안을 지우지 못했어요. 탭을 닫아 임시 초안을 지운 뒤 다른 기기 기록도 확인해 주세요.'); return; }
    await Promise.all(LOCAL_DATABASES.map(deleteLocalDatabase));
    setCleared(true);
    } catch { setError(clearedText ? '글 기록은 비웠지만 사진과 전송함의 삭제를 끝내지 못했어요. 다른 앉아봄 탭을 닫고 다시 눌러 주세요.' : '기기 기록 삭제를 확인하지 못했어요. 저장 공간과 다른 탭을 확인하고 다시 눌러 주세요.'); }
  };

  const exportData = async () => {
    const response = await fetch('/api/export', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ format: 'csv' }),
    });
    if (!response.ok) {
      setError('내보내기 파일을 만들지 못했어요.');
      return;
    }
    const url = URL.createObjectURL(await response.blob());
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = `anjabom-export-${new Date().toISOString().slice(0, 10).replaceAll('-', '')}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const remove = async () => {
    if (!mode) return;
    setError('');
    const response = await fetch('/api/me', {
      method: 'DELETE',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ confirm: true, mode }),
    });
    const value: unknown = await response.json();
    if (!response.ok) {
      setError('계정 삭제를 끝내지 못했어요. 다시 확인해 주세요.');
      return;
    }
    const nextReceipt = parseDeletionReceipt(value);
    if (!nextReceipt || nextReceipt.mode !== mode) {
      setError('삭제 결과를 확인하지 못했어요. 계정 상태를 다시 확인해 주세요.');
      return;
    }
    setReceipt(nextReceipt);
    setSession(false);
  };

  return <section className="page narrow stack-lg">
    <div>
      <p className="eyebrow">데이터와 계정</p>
      <h1>내 기록을 직접 관리해요.</h1>
      <p className="lede">{appsInToss ? '이 기기의 기록을 백업하거나 지울 수 있어요.' : '기기 초안과 서버 계정은 따로 관리해요. 서버의 삭제 결과를 확인한 뒤에만 완료로 표시해요.'}</p>
    </div>

    <Link className="button" to="/pass?trigger=settings">시즌 패스 안내 보기</Link><PortfolioThemeControl /><LocalBackupPanel /><p>공개한 글은 먼저 지워 주세요. <Link to="/me/posts">내가 공개한 글</Link></p>

    <article className="card stack">
      <h2>이 기기의 데이터</h2>
      <p className="muted">후보 좌석, 내 조건, 리뷰 초안, 오프라인 전송함과 기기에만 둔 관람 사진을 지워요.</p>
      <button className="button button-danger" onClick={() => void clear()}>이 기기의 앉아봄 데이터 지우기</button>
      {cleared ? <p role="status">이 기기에서 앉아봄 데이터를 지웠어요.</p> : null}
    </article>

    {appsInToss ? null : session ? <>
      <article className="card stack">
        <h2>내 기록 내보내기</h2>
        <p className="muted">엑셀에서 열 수 있는 UTF-8 CSV예요. 수식으로 실행될 수 있는 셀은 안전하게 가려요.</p>
        <button className="button button-secondary" onClick={() => void exportData()}>CSV 내려받기</button>
      </article>
      <article className="card stack">
        <h2>계정 삭제</h2>
        <p>둘 중 하나를 직접 골라 주세요. 기본 선택은 없어요.</p>
        <label className="candidate">
          <span><strong>계정만 삭제해요</strong><br /><span className="micro">로그인·프로필·개인 기록은 지우고 게시 리뷰는 작성자 없는 글로 남겨요.</span></span>
          <input type="radio" checked={mode === 'account_only'} onChange={() => setMode('account_only')} />
        </label>
        <label className="candidate">
          <span><strong>계정과 게시물도 삭제해요</strong><br /><span className="micro">리뷰·가격·정정·사진을 제거하고 법적 최소 기록만 제한적으로 남길 수 있어요.</span></span>
          <input type="radio" checked={mode === 'account_and_content'} onChange={() => setMode('account_and_content')} />
        </label>
        <button className="button button-danger" disabled={!mode} onClick={() => void remove()}>선택한 방식으로 삭제하기</button>
      </article>
    </> : <article className="card stack">
      <h2>서버 계정</h2>
      <p className="muted">로그인하면 게시 리뷰와 동기화한 기록을 내보내거나 삭제할 수 있어요.</p>
      <a className="button button-secondary" href="/api/auth/google/start?returnTo=%2Fsettings">로그인하기</a>
    </article>}

    {receipt ? <DeletionReceiptCard receipt={receipt} /> : null}
    {error ? <p role="alert">{error}</p> : null}
  </section>;
}

function DeletionReceiptCard({ receipt }: { receipt: DeletionReceipt }) {
  const summary = summarizeDeletionReceipt(receipt);
  return <article className="card stack" aria-labelledby="deletion-result-title">
    <h2 id="deletion-result-title">삭제 결과</h2>
    <p role="status">{summary.headline}</p>
    <ReceiptSectionView title="바로 지운 데이터" section={summary.deleted} />
    {summary.retained.count > 0 ? <ReceiptSectionView title="남겨 둔 데이터" section={summary.retained} /> : null}
    {summary.pendingPurge.count > 0 ? <ReceiptSectionView title="지우는 중인 데이터" section={summary.pendingPurge} /> : null}
  </article>;
}

function ReceiptSectionView({ title, section }: { title: string; section: ReceiptSection }) {
  return <section>
    <h3>{title} · {section.count}종</h3>
    <ul>{section.labels.map((label) => <li key={label}>{label}</li>)}</ul>
  </section>;
}
